"use client";
/**
 * FlowMap - the universal flow map: a canvas of steps joined by arrows plus a
 * table view of the same map. The page-builder widget shows it read-only on the
 * page and editable in the builder's map editor; the CRM guides and projects
 * edit and save it through their own API.
 *
 * - `value` is any JSON, normalised once per change. The map is editable when
 *   `onChange` is given and `readOnly` is not set.
 * - Every edit goes through `commit`: an edit that changes nothing does nothing;
 *   a change is recorded for undo (when history is on) and handed to `onChange`
 *   ONCE per finished gesture. Undo and redo hand back a stored document.
 * - The server HTML and the first client render show the static picture
 *   (FlowMapPicture). React Flow is a separate chunk (FlowMapCanvas, React.lazy)
 *   that replaces the picture once the map is mounted and near the screen, so
 *   pages without a map, the API and the node tests never load it.
 * - The canvas, the table and the side panel share one selection.
 * - Keys (focus inside the map): Ctrl/Cmd+Z undo, Shift+Ctrl/Cmd+Z or Ctrl+Y
 *   redo, Delete / Backspace remove what is selected, Escape clears the
 *   selection. Keys the map handles, and plain letters typed on the canvas or
 *   the table, stop here, so host shortcuts (the builder's "t" and "x") do not
 *   fire behind the map.
 *
 * Text: the UI copy follows `locale`; the map's own text is shown and edited in
 * `textLocale` when it is set (the builder's content language), else in `locale`.
 */
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
} from "react";
import { whenIdleAndVisible } from "../idle.js";
import { CanvasBoundary } from "./CanvasBoundary.js";
import { liveSelection, placeNewNode, sameSelection, type FlowSelection, type FlowViewport } from "./canvasModel.js";
import { COMBO_CSS } from "../combo/css.js";
import { FM_CSS } from "./css.js";
import type { FlowCanvasApi } from "./FlowMapCanvas.js";
import { FlowMapPicture, sanitizeIdPrefix } from "./FlowMapPicture.js";
import { FlowMapTable } from "./FlowMapTable.js";
import { Inspector } from "./Inspector.js";
import type { EditorEnv } from "./inspectorFields.js";
import { flowMapLabels } from "./labels.js";
import { autoLayout } from "./layout.js";
import { FM_LIVE_CSS } from "./liveCss.js";
import {
  FLOW_LIMITS,
  FLOW_VIEWS,
  addNode,
  normalizeFlowDoc,
  removeEdges,
  removeNodes,
  writeText,
  type FlowDirection,
  type FlowMapDoc,
  type FlowMapLabels,
  type FlowNodeKind,
  type FlowPoint,
  type FlowView,
} from "./model.js";
import type { LinkHandlers } from "./NodeDetails.js";
import type { FlowMapProps } from "./props.js";
import { Toolbar } from "./Toolbar.js";
import { useFlowHistory } from "./useFlowHistory.js";

const FlowMapCanvas = lazy(() => import("./FlowMapCanvas.js"));

// The pickers are the widgets' own pick-one list: its sheet first, so the editor's rules win.
const STYLE = COMBO_CSS + FM_CSS + FM_LIVE_CSS;
/** What the side panel covers on the canvas's right edge while it floats over it (300px plus margins). */
const PANEL_INSET = 320;
/** Below this width the panel drops under the canvas (the container query in liveCss.ts). */
const WIDE = 720;
/** Maps up to this many nodes keep the "how to edit" hint on screen. */
const HINT_UNTIL = 3;

const isView = (v: unknown): v is FlowView => typeof v === "string" && (FLOW_VIEWS as readonly string[]).includes(v);

function cssHeight(h: number | string | undefined): string | undefined {
  if (typeof h === "number") return Number.isFinite(h) ? `${Math.round(Math.min(4000, Math.max(160, h)))}px` : undefined;
  if (typeof h === "string" && h.trim()) return h.trim();
  return undefined;
}

/** A key typed into a form field belongs to the field, not to the map. */
function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable;
}

/** Input types that do not take typed text; Ctrl/Cmd+Z there means the map's undo, not the field's. */
const NON_TEXT_INPUTS = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"]);

/** A field whose own undo owns Ctrl/Cmd+Z: a textarea, an editable element, or a text-like input. */
function isTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  if (el.tagName === "TEXTAREA" || el.isContentEditable) return true;
  return el.tagName === "INPUT" && !NON_TEXT_INPUTS.has((el as HTMLInputElement).type);
}

function within(target: EventTarget | null, selector: string): boolean {
  const el = target as Element | null;
  return !!el && typeof el.closest === "function" && el.closest(selector) !== null;
}

function shallowEqual(a: object | undefined, b: object | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => (a as Record<string, unknown>)[k] === (b as Record<string, unknown>)[k]);
}

/** The label set, kept as the same object while the locale and the overrides stay the same (cheap React deps). */
function useStableLabels(locale: string | undefined, overrides: Partial<FlowMapLabels> | undefined): FlowMapLabels {
  const memo = useRef<{ locale: string | undefined; overrides: Partial<FlowMapLabels> | undefined; labels: FlowMapLabels } | null>(null);
  const m = memo.current;
  if (m && m.locale === locale && shallowEqual(m.overrides, overrides)) return m.labels;
  const labels = flowMapLabels(locale, overrides);
  memo.current = { locale, overrides, labels };
  return labels;
}

function afterTwoFrames(fn: () => void): void {
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => requestAnimationFrame(fn));
  else setTimeout(fn, 32);
}

/**
 * Canvases take turns: one React Flow mounts, the next one two frames later. A page with
 * several maps then never builds them all in one long frame (which also trips the browser's
 * "ResizeObserver loop" notice while all their nodes are measured together).
 */
const mountQueue: Array<() => void> = [];
let draining = false;

function drainMounts(): void {
  const next = mountQueue.shift();
  if (!next) {
    draining = false;
    return;
  }
  try {
    next();
  } finally {
    afterTwoFrames(drainMounts);
  }
}

function queueMount(fn: () => void): () => void {
  mountQueue.push(fn);
  if (!draining) {
    draining = true;
    drainMounts();
  }
  return () => {
    const i = mountQueue.indexOf(fn);
    if (i !== -1) mountQueue.splice(i, 1);
  };
}

/**
 * True once the live canvas may replace the picture: the stage is near the screen, the
 * browser has been idle once (the server-rendered picture stays the page's first paint, and
 * React Flow's chunk loads after it), and it is this map's turn.
 */
function useCanvasTurn(ref: RefObject<HTMLElement>, active: boolean): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready || !active) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver !== "function") {
      setReady(true);
      return;
    }
    let dequeue: (() => void) | null = null;
    const stop = whenIdleAndVisible(el, () => {
      dequeue = queueMount(() => setReady(true));
    }, "400px");
    return () => {
      stop();
      dequeue?.();
    };
  }, [ready, active, ref]);
  return ready;
}

export function FlowMap(props: FlowMapProps): ReactElement {
  const { value, onChange, readOnly, locale, textLocale, textDefaultLocale, LinkField, toolbarExtra, onOpenLink, resolveHref, className, ariaLabel } = props;
  const editable = !!onChange && !readOnly;
  const historyOn = editable && props.history !== false;
  const scrollZoom = props.scrollZoom ?? editable;
  const contentLocale = textLocale ?? locale;
  const labels = useStableLabels(locale, props.labels);
  const uid = `wa-fm-${sanitizeIdPrefix(useId())}`;
  const hintId = `${uid}-hint`;

  // ---- the document -----------------------------------------------------------
  /** The last document this component handed to onChange (its round trip skips normalising). */
  const emitted = useRef<FlowMapDoc | null>(null);
  const doc = useMemo(() => (value != null && value === emitted.current ? (value as FlowMapDoc) : normalizeFlowDoc(value)), [value]);
  /** The latest document: `doc`, or what was just committed while the host has not re-rendered yet. */
  const docRef = useRef(doc);
  const seenDoc = useRef(doc);
  if (seenDoc.current !== doc) {
    seenDoc.current = doc;
    docRef.current = doc;
  }

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editableRef = useRef(editable);
  editableRef.current = editable;
  const { canUndo, canRedo, record, undo: popUndo, redo: popRedo, clear } = useFlowHistory(historyOn);

  const emit = useCallback((next: FlowMapDoc): void => {
    docRef.current = next;
    emitted.current = next;
    onChangeRef.current?.(next);
  }, []);

  const commit = useCallback(
    (next: FlowMapDoc): boolean => {
      const cur = docRef.current;
      if (next === cur || !editableRef.current) return false;
      record(cur);
      emit(next);
      return true;
    },
    [record, emit],
  );

  const edit = useCallback((fn: (d: FlowMapDoc) => FlowMapDoc): boolean => commit(fn(docRef.current)), [commit]);
  const undo = useCallback((): void => {
    const target = popUndo(docRef.current);
    if (target) emit(target);
  }, [popUndo, emit]);
  const redo = useCallback((): void => {
    const target = popRedo(docRef.current);
    if (target) emit(target);
  }, [popRedo, emit]);

  // A document replaced from outside (the CRM's "load the latest", another tab) is not ours to undo.
  const known = useRef<FlowMapDoc | null>(null);
  useEffect(() => {
    const prev = known.current;
    known.current = doc;
    if (!prev || doc === prev || doc === emitted.current) return;
    const json = JSON.stringify(doc);
    if (json === JSON.stringify(prev) || (emitted.current && json === JSON.stringify(emitted.current))) return;
    clear();
  }, [doc, clear]);

  // ---- view and selection -------------------------------------------------------
  const [ownView, setOwnView] = useState<FlowView>(() => (isView(props.defaultView) ? props.defaultView : "canvas"));
  const controlled = isView(props.view);
  const view: FlowView = isView(props.view) ? props.view : ownView;
  const viewRef = useRef(view);
  viewRef.current = view;
  const controlledRef = useRef(controlled);
  controlledRef.current = controlled;
  const onViewChangeRef = useRef(props.onViewChange);
  onViewChangeRef.current = props.onViewChange;

  const setView = useCallback((v: FlowView): void => {
    if (v === viewRef.current) return;
    if (!controlledRef.current) setOwnView(v);
    onViewChangeRef.current?.(v);
  }, []);

  const [rawSelection, setRawSelection] = useState<FlowSelection | null>(null);
  const selection = liveSelection(doc, rawSelection);
  const selRef = useRef(selection);
  selRef.current = selection;
  /** A node that was just added: its title field takes the focus. */
  const [freshId, setFreshId] = useState<string | null>(null);
  const select = useCallback((next: FlowSelection | null): void => {
    setRawSelection((cur) => (sameSelection(cur, next) ? cur : next));
    setFreshId(null);
  }, []);

  /** A node to centre once the canvas is up (coming back from the table). */
  const [focusId, setFocusId] = useState<string | null>(null);
  const lastView = useRef(view);
  useEffect(() => {
    const sel = selRef.current;
    if (lastView.current !== "canvas" && view === "canvas" && sel?.type === "node") setFocusId(sel.id);
    lastView.current = view;
  }, [view]);

  // ---- the canvas -----------------------------------------------------------------
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<FlowCanvasApi | null>(null);
  const viewportRef = useRef<FlowViewport | undefined>(undefined);
  const [canvasReady, setCanvasReady] = useState(false);
  const near = useCanvasTurn(stageRef, view === "canvas");
  const panelInset = useCallback((): number => ((rootRef.current?.clientWidth ?? 0) > WIDE ? PANEL_INSET : 0), []);
  /** Fit the map, clear of the side panel while it is open. */
  const fitView = useCallback((): void => apiRef.current?.fitView(selRef.current ? panelInset() : 0), [panelInset]);

  const goToNode = useCallback(
    (id: string): void => {
      select({ type: "node", id });
      if (viewRef.current === "canvas") apiRef.current?.centerOn(id, panelInset());
    },
    [select, panelInset],
  );
  const showOnMap = useCallback(
    (id: string): void => {
      select({ type: "node", id });
      if (viewRef.current === "canvas") apiRef.current?.centerOn(id, panelInset());
      else setView("canvas");
    },
    [select, panelInset, setView],
  );

  // Link callbacks go through refs, so an inline function from the host does not re-render every card.
  const hrefRef = useRef(resolveHref);
  hrefRef.current = resolveHref;
  const openRef = useRef(onOpenLink);
  openRef.current = onOpenLink;
  const rewrites = !!resolveHref;
  const links = useMemo<LinkHandlers>(
    () => ({
      resolveHref: rewrites ? (href) => hrefRef.current?.(href) ?? href : undefined,
      onOpenLink: (link, node) => openRef.current?.(link, node),
      onGoToNode: goToNode,
    }),
    [rewrites, goToNode],
  );

  // ---- actions ----------------------------------------------------------------------
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  const textLocaleRef = useRef(textLocale);
  textLocaleRef.current = textLocale;

  const addAt = useCallback(
    (kind: FlowNodeKind, at?: FlowPoint | null): void => {
      const d = docRef.current;
      const centre = at ?? (viewRef.current === "canvas" ? (apiRef.current?.visibleCentre(panelInset()) ?? null) : null);
      const l = labelsRef.current;
      const title = kind === "start" ? l.kindStart : kind === "end" ? l.kindEnd : "";
      const added = addNode(d, { kind, position: placeNewNode(d, kind, centre), title: writeText(undefined, title, textLocaleRef.current) }, { random: true });
      if (!added.id || !commit(added.doc)) return;
      select({ type: "node", id: added.id });
      setFreshId(added.id);
    },
    [commit, select, panelInset],
  );

  const deleteSelected = useCallback((): boolean => {
    const picked = viewRef.current === "canvas" ? apiRef.current?.selectedIds() : undefined;
    let nodes = picked?.nodes ?? [];
    let edges = picked?.edges ?? [];
    const sel = selRef.current;
    if (!nodes.length && !edges.length && sel) {
      if (sel.type === "node") nodes = [sel.id];
      else edges = [sel.id];
    }
    if (!nodes.length && !edges.length) return false;
    const changed = edit((d) => removeEdges(removeNodes(d, nodes), edges));
    if (changed) select(null);
    return changed;
  }, [edit, select]);

  const arrange = useCallback(
    (direction: FlowDirection): void => {
      edit((d) => autoLayout(d, { direction }));
      if (viewRef.current === "canvas") fitView();
    },
    [edit, fitView],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.defaultPrevented) return;
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    // Undo and redo are the map's everywhere but a text field, whose own undo owns
    // the keys; a checkbox, a radio or a select has none and must not swallow them.
    if (historyOn && mod && !e.altKey && (key === "z" || key === "y") && !isTextEntry(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      if (key === "y" || e.shiftKey) redo();
      else undo();
      return;
    }
    if (isTyping(e.target)) return;
    const onMap = within(e.target, ".wa-fm__canvas") || within(e.target, ".wa-fm-table");
    if (!onMap) return;
    if (editable && !mod && (e.key === "Delete" || e.key === "Backspace")) {
      if (deleteSelected()) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    if (e.key === "Escape") {
      if (selRef.current) select(null);
      return;
    }
    if (!mod && !e.altKey && e.key.length === 1 && e.key !== " ") e.stopPropagation();
  };

  // ---- render ---------------------------------------------------------------------------
  const env: EditorEnv = { doc, labels, locale: contentLocale, textLocale, textDefaultLocale, LinkField, refChoices: props.refChoices, edit };
  const empty = doc.nodes.length === 0;
  const marks = props.nodeMarks;
  const picture = empty ? null : <FlowMapPicture doc={doc} locale={contentLocale} labels={labels} idPrefix={uid} marks={marks} />;

  const stage = (
    <div ref={stageRef} className="wa-fm__stage">
      {near ? (
        // A chunk that fails to load (a tab left open across a deploy) leaves the
        // static picture in place instead of taking the host page down.
        <CanvasBoundary fallback={picture}>
          <Suspense fallback={picture}>
            <FlowMapCanvas
              doc={doc}
              locale={contentLocale}
              labels={labels}
              editable={editable}
              scrollZoom={scrollZoom}
              minimap={props.minimap === true}
              selection={selection}
              idPrefix={uid}
              marks={marks}
              apiRef={apiRef}
              onReady={setCanvasReady}
              onSelect={select}
              edit={edit}
              onAddAt={addAt}
              links={links}
              viewport={viewportRef.current}
              onViewportChange={(vp) => {
                viewportRef.current = vp;
              }}
              focusNodeId={focusId}
              panelInset={panelInset()}
              onFocused={() => setFocusId(null)}
              ariaLabel={labels.canvasView}
              describedBy={editable ? hintId : undefined}
            />
          </Suspense>
        </CanvasBoundary>
      ) : (
        picture
      )}
      {empty ? (
        <div className="wa-fm__empty wa-fm__empty--live">
          {editable ? (
            <div>
              <div className="wa-fm__empty-title">{labels.emptyTitle}</div>
              <div>{labels.emptyHint}</div>
            </div>
          ) : (
            labels.emptyReadOnly
          )}
        </div>
      ) : null}
      {editable ? (
        <div id={hintId} className={!empty && doc.nodes.length <= HINT_UNTIL ? "wa-fm__hint" : "wa-fm__sr"}>
          {labels.canvasHint}
        </div>
      ) : null}
    </div>
  );

  const main =
    view === "canvas" ? (
      stage
    ) : (
      <FlowMapTable
        doc={doc}
        locale={contentLocale}
        labels={labels}
        selectedId={selection?.type === "node" ? selection.id : null}
        onSelectNode={(id) => select({ type: "node", id })}
        emptyText={editable ? labels.emptyTitle : labels.emptyReadOnly}
        caption={ariaLabel}
        marks={marks}
      />
    );

  const rootStyle = { "--wa-fm-h": cssHeight(props.height) } as CSSProperties;
  const rootClass = `wa-fm wa-fm--live${editable ? " is-editable" : ""}${className ? ` ${className}` : ""}`;

  return (
    <div
      ref={rootRef}
      className={rootClass}
      style={rootStyle}
      role={ariaLabel ? "region" : undefined}
      aria-label={ariaLabel || undefined}
      onKeyDown={onKeyDown}
    >
      <style dangerouslySetInnerHTML={{ __html: STYLE }} />
      <Toolbar
        labels={labels}
        view={view}
        showSwitch={props.showViewToggle !== false}
        onView={setView}
        editable={editable}
        canvasReady={canvasReady}
        onAdd={(kind) => addAt(kind)}
        canAdd={doc.nodes.length < FLOW_LIMITS.nodes}
        onArrange={arrange}
        canArrange={!empty}
        onDelete={() => void deleteSelected()}
        canDelete={selection !== null}
        onFit={fitView}
        onZoomIn={() => apiRef.current?.zoomIn()}
        onZoomOut={() => apiRef.current?.zoomOut()}
        history={historyOn ? { canUndo, canRedo, onUndo: undo, onRedo: redo } : null}
        extra={toolbarExtra}
      />
      <div className={`wa-fm__body wa-fm__body--${view}${selection ? " has-inspector" : ""}`}>
        <div className="wa-fm__main">{main}</div>
        {selection ? (
          <Inspector
            env={env}
            selection={selection}
            editable={editable}
            view={view}
            links={links}
            onSelect={select}
            onShowOnMap={showOnMap}
            focusTitle={selection.type === "node" && selection.id === freshId}
          />
        ) : null}
      </div>
    </div>
  );
}
